/**
 * Exam configuration and utility functions
 */

// PMP Block configuration: 3 blocks of 60 questions with 10-min breaks
export const PMP_BLOCK_SIZE = 60;
export const PMP_BREAK_MINUTES = 10;

// Default exam configs (will be overridden by actual question counts)
export const defaultExamConfigs = {
  PMP: { 
    name: 'PMP', 
    fullName: 'Project Management Professional', 
    targetQuestions: 180, 
    time: 230, 
    hasBlocks: true, 
    blockSize: 60, 
    breakMinutes: 10 
  },
  CAPM: { 
    name: 'CAPM', 
    fullName: 'Certified Associate in PM', 
    targetQuestions: 150, 
    time: 180, 
    hasBlocks: false 
  },
  PSM: { 
    name: 'PSM', 
    fullName: 'Professional Scrum Master', 
    targetQuestions: 80, 
    time: 60, 
    hasBlocks: false 
  },
  PSPO: { 
    name: 'PSPO', 
    fullName: 'Professional Scrum Product Owner', 
    targetQuestions: 80, 
    time: 60, 
    hasBlocks: false 
  },
};

/**
 * Get actual exam config with real question counts
 */
export const getExamConfig = (track, questionStats) => {
  const defaultConfig = defaultExamConfigs[track] || defaultExamConfigs.PMP;
  const availableQuestions = questionStats[track] || 0;
  
  // Use minimum of target and available questions
  const actualQuestions = availableQuestions > 0 
    ? Math.min(defaultConfig.targetQuestions, availableQuestions) 
    : defaultConfig.targetQuestions;
  
  // Adjust time proportionally if we have fewer questions
  const timeRatio = actualQuestions / defaultConfig.targetQuestions;
  const adjustedTime = Math.max(Math.round(defaultConfig.time * timeRatio), 10);
  
  return {
    ...defaultConfig,
    questions: actualQuestions,
    time: adjustedTime,
    hasEnoughQuestions: availableQuestions >= defaultConfig.targetQuestions
  };
};

/**
 * Format seconds to readable time string
 */
export const formatTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Get current block info for PMP exams
 */
export const getCurrentBlockInfo = (examType, examConfigs, currentBlock) => {
  const config = examConfigs[examType];
  if (!config?.hasBlocks) return null;
  
  const blockSize = config.blockSize || PMP_BLOCK_SIZE;
  const totalBlocks = Math.ceil(config.questions / blockSize);
  const blockStart = (currentBlock - 1) * blockSize;
  const blockEnd = Math.min(currentBlock * blockSize, config.questions);
  
  return {
    currentBlock,
    totalBlocks,
    blockStart,
    blockEnd,
    blockSize: blockEnd - blockStart,
    isLastBlock: currentBlock >= totalBlocks
  };
};

/**
 * Check if question is in current block (for PMP)
 */
export const isQuestionInCurrentBlock = (questionIndex, blockInfo) => {
  if (!blockInfo) return true; // No blocks = all questions accessible
  return questionIndex >= blockInfo.blockStart && questionIndex < blockInfo.blockEnd;
};

/**
 * Check if question is in a completed block
 */
export const isQuestionInCompletedBlock = (questionIndex, currentBlock, blockSize = PMP_BLOCK_SIZE) => {
  const questionBlock = Math.floor(questionIndex / blockSize) + 1;
  return questionBlock < currentBlock;
};
